import { Box, Modal } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { IconUpload, IconX } from "@tabler/icons-react";
import Button from "../extra/Button";
import Input from "../extra/Input";
import { CLOSE_DIALOGUE } from "../store/dialogue/dialogue.type";
import {
  sendNotificationToAllUsers,
  sendNotificationToUser,
} from "../store/notification/notification.action";
import { uploadFile } from "../../util/AwsFunction";
import { permissionError } from "../../util/Alert";

const NOTIFICATION_DIALOGUE_TYPES = ["sendNotification", "sendNotificationAll"];

function SendNotificationDialog(props) {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const { dialogue, dialogueType, dialogueData, isSubmitting } = useSelector(
    (state) => state.dialogue
  );
  const hasPermission = useSelector((state) => state.admin.admin.flag);

  const isAllUsersMode = dialogueType === "sendNotificationAll";

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [error, setError] = useState({
    title: "",
    message: "",
    image: "",
  });

  useEffect(() => {
    const isOpen =
      dialogue && NOTIFICATION_DIALOGUE_TYPES.includes(dialogueType);
    setOpen(isOpen);

    if (isOpen) {
      setTitle("");
      setMessage("");
      setImageFile(null);
      setImagePreview("");
      setError({ title: "", message: "", image: "" });
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [dialogue, dialogueType, dialogueData]);

  const handleClose = () => {
    setOpen(false);
    setTitle("");
    setMessage("");
    setImageFile(null);
    setImagePreview("");
    setError({ title: "", message: "", image: "" });
    if (fileInputRef.current) fileInputRef.current.value = "";

    dispatch({
      type: CLOSE_DIALOGUE,
      payload: {
        dialogue: false,
      },
    });

    sessionStorage.setItem(
      "dialogueData",
      JSON.stringify({ dialogue: false })
    );
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type?.startsWith("image/")) {
      setError((prev) => ({
        ...prev,
        image: "Only image files are allowed",
      }));
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setError((prev) => ({ ...prev, image: "" }));
  };

  const handleSubmit = async () => {
    if (!hasPermission) return permissionError();

    const validationErrors = {};
    if (!title?.trim()) validationErrors.title = "Title is required";
    if (!message?.trim()) validationErrors.message = "Message is required";

    if (Object.keys(validationErrors).length > 0) {
      return setError((prev) => ({ ...prev, ...validationErrors }));
    }

    try {
      let imageUrl = "";
      if (imageFile) {
        const { resDataUrl } = await uploadFile(
          imageFile,
          "notification",
          dispatch,
          "notification-image"
        );
        imageUrl = resDataUrl;
      }

      const payload = {
        title: title.trim(),
        message: message.trim(),
      };

      if (imageUrl) {
        payload.image = imageUrl;
      }

      let res;
      if (isAllUsersMode) {
        res = await props.sendNotificationToAllUsers(payload);
      } else {
        payload.userId = dialogueData?._id;
        res = await props.sendNotificationToUser(payload);
      }

      if (res?.data?.status) {
        handleClose();
      }
    } catch (err) {
      console.error("Send notification failed:", err);
    }
  };

  if (!NOTIFICATION_DIALOGUE_TYPES.includes(dialogueType)) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="send-notification-title"
      aria-describedby="send-notification-description"
    >
      <Box className="model-style send-notification-dialog">
        <div className="model-header d-flex align-items-center justify-content-between">
          <p className="m-0" id="send-notification-title">
            Send Notification
          </p>
          <button
            type="button"
            className="send-notification-dialog__close"
            onClick={handleClose}
            aria-label="Close"
          >
            <IconX size={18} />
          </button>
        </div>

        <div className="model-body">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            {isAllUsersMode && (
              <Input
                label={"All Users"}
                name={"recipient"}
                id={"notification-recipient"}
                value={"All Users"}
                disabled={true}
                readOnly={true}
              />
            )}

            <Input
              label={"Title"}
              name={"title"}
              placeholder={"Title"}
              value={title}
              errorMessage={error.title}
              onChange={(e) => {
                setTitle(e.target.value);
                setError((prev) => ({
                  ...prev,
                  title: e.target.value?.trim() ? "" : "Title is required",
                }));
              }}
            />

            <div className="custom-input mt-2">
              <label htmlFor="notification-message">Message</label>
              <textarea
                id="notification-message"
                cols={1}
                rows={4}
                className="form-control"
                value={message}
                placeholder={"Message"}
                onChange={(e) => {
                  setMessage(e.target.value);
                  setError((prev) => ({
                    ...prev,
                    message: e.target.value?.trim()
                      ? ""
                      : "Message is required",
                  }));
                }}
              />
              {error.message && (
                <p className="errorMessage">{error.message}</p>
              )}
            </div>

            <div className="mt-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="d-none"
                onChange={handleImageChange}
              />
              <button
                type="button"
                className="send-notification-dialog__upload"
                onClick={() => fileInputRef.current?.click()}
              >
                <IconUpload size={18} />
                <span>Upload File</span>
              </button>
              <p className="extention-show mb-0 mt-2">
                Accepted formats: image/*
              </p>
              {error.image && <p className="errorMessage">{error.image}</p>}
              {imagePreview && (
                <div className="mt-2 fake-create-img mb-1">
                  <img
                    src={imagePreview}
                    alt="notification preview"
                    style={{
                      width: "96px",
                      height: "auto",
                      objectFit: "contain",
                      borderRadius: "6px",
                    }}
                  />
                </div>
              )}
            </div>
          </form>
        </div>

        <div className="model-footer">
          <div className="m-3 d-flex justify-content-end">
            <Button
              onClick={handleClose}
              btnName={"Cancel"}
              newClass={"send-notification-dialog__cancel me-2"}
              disabled={isSubmitting}
            />
            <Button
              onClick={handleSubmit}
              btnName={"Send"}
              type={"button"}
              newClass={"submit-btn"}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default connect(null, {
  sendNotificationToUser,
  sendNotificationToAllUsers,
})(SendNotificationDialog);
